import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { PrimaryButton } from '@components/library/button';

import classes from './generated-key-panel.module.scss';

const COPY_FEEDBACK_DURATION_MS = 2000;

interface GeneratedKeyPanelProps {
  /** Called once the user clicks Done (only enabled after copying). */
  onDone: () => void;
  /** One-time plaintext token to reveal. */
  token: string;
}

/**
 * The shared "New API Key Generated" step used by both the Create and Rotate
 * modals: reveals the one-time token, lets the user copy it, and gates Done
 * behind a copy so the key isn't lost.
 */
export const GeneratedKeyPanel: FunctionComponent<GeneratedKeyPanelProps> = ({ onDone, token }) => {
  const [hasCopiedOnce, setHasCopiedOnce] = useState(false);
  const [isCopyFeedbackVisible, setIsCopyFeedbackVisible] = useState(false);
  const copyFeedbackTimeoutRef = useRef<number | null>(null);

  const clearCopyFeedbackTimeout = (): void => {
    if (copyFeedbackTimeoutRef.current !== null) {
      window.clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = null;
    }
  };

  useEffect(() => (): void => {
    clearCopyFeedbackTimeout();
  }, []);

  const handleCopy = async (): Promise<void> => {
    if (!token) return;

    try {
      if (window.navigator?.clipboard) {
        await window.navigator.clipboard.writeText(token);
      } else {
        const textField = document.createElement('textarea');
        textField.value = token;
        document.body.appendChild(textField);
        textField.select();
        document.execCommand('copy');
        textField.remove();
      }
      setHasCopiedOnce(true);
      setIsCopyFeedbackVisible(true);
      clearCopyFeedbackTimeout();
      copyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setIsCopyFeedbackVisible(false);
        copyFeedbackTimeoutRef.current = null;
      }, COPY_FEEDBACK_DURATION_MS);
    } catch {
      Notify.create({
        title: t`Unable to copy API key`,
        type: 'error',
      });
    }
  };

  const handleDone = (): void => {
    if (!hasCopiedOnce) return;
    onDone();
  };

  return (
    <div className={classes.generatedContent}>
      <p className={classes.securityNotice}>
        <Trans>
          Copy this key and store it securely now. This is the only time it will be shown — after you close this
          window, it cannot be viewed or copied again.
        </Trans>
      </p>

      <div className={classes.keyField}>
        <span className={classes.keyLabel} id="generated-api-key-label">
          <Trans>Key</Trans>
        </span>
        <div className={classes.keyInputRow}>
          <div
            aria-labelledby="generated-api-key-label"
            className={classes.keyInput}
            id="generated-api-key"
            onCopy={(event): void => event.preventDefault()}
          >
            {token}
          </div>
          <button
            aria-label={isCopyFeedbackVisible ? t`Copied` : t`Copy API key`}
            className={classes.copyButton}
            onClick={(): void => { void handleCopy(); }}
            type="button"
          >
            {isCopyFeedbackVisible ? (
              <>
                <svg aria-hidden="true" className={classes.copiedCheck} viewBox="0 0 16 16">
                  <circle cx="8" cy="8" fill="currentColor" r="8" />
                  <path
                    d="M4.6 8.2 6.9 10.5 11.4 5.6"
                    fill="none"
                    stroke="#fff"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.6"
                  />
                </svg>
                <Trans>Copied</Trans>
              </>
            ) : (
              <Trans>Copy</Trans>
            )}
          </button>
        </div>
      </div>

      <div className={classes.actions}>
        <PrimaryButton
          className={classes.doneButton}
          disabled={!hasCopiedOnce}
          onClick={handleDone}
          size="small"
          type="button"
        >
          <Trans>Done</Trans>
        </PrimaryButton>
      </div>
    </div>
  );
};
